import { useState, useCallback, useEffect, useRef, createContext, useContext } from 'react';
import SafeViewportOverlay from '@/components/common/SafeViewportOverlay';
import { useBodyScrollLock } from '@/hooks/useBodyScrollLock';

interface ConfirmOptions {
  title?: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  destructive?: boolean;
}

interface ConfirmDialogContextType {
  confirm: (options: ConfirmOptions | string) => Promise<boolean>;
}

const ConfirmDialogContext = createContext<ConfirmDialogContextType | null>(null);

export function useConfirmDialog() {
  const ctx = useContext(ConfirmDialogContext);
  if (!ctx) throw new Error('useConfirmDialog must be used within ConfirmDialogProvider');
  return ctx.confirm;
}

export function ConfirmDialogProvider({ children }: { children: React.ReactNode }) {
  const [options, setOptions] = useState<ConfirmOptions | null>(null);
  const [closing, setClosing] = useState(false);
  const resolveRef = useRef<((value: boolean) => void) | null>(null);
  const closeTimeoutRef = useRef<number | null>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  const cancelButtonRef = useRef<HTMLButtonElement>(null);
  useBodyScrollLock(options !== null);

  const confirm = useCallback((opts: ConfirmOptions | string): Promise<boolean> => {
    const normalized = typeof opts === 'string' ? { message: opts } : opts;
    if (closeTimeoutRef.current !== null) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
    setClosing(false);
    setOptions(normalized);
    return new Promise<boolean>((resolve) => {
      resolveRef.current = resolve;
    });
  }, []);

  const handleClose = useCallback((result: boolean) => {
    resolveRef.current?.(result);
    resolveRef.current = null;
    setClosing(true);
    closeTimeoutRef.current = window.setTimeout(() => {
      setOptions(null);
      setClosing(false);
      closeTimeoutRef.current = null;
    }, 200);
  }, []);

  useEffect(() => {
    if (!options) return;
    const previouslyFocused = document.activeElement instanceof HTMLElement
      ? document.activeElement
      : null;
    const focusFrame = window.requestAnimationFrame(() => cancelButtonRef.current?.focus());

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        handleClose(false);
        return;
      }
      if (event.key !== 'Tab' || !dialogRef.current) return;
      const focusable = Array.from(dialogRef.current.querySelectorAll<HTMLElement>(
        'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
      )).filter(element => element.offsetParent !== null);
      if (focusable.length === 0) {
        event.preventDefault();
        dialogRef.current.focus();
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && (document.activeElement === first || document.activeElement === dialogRef.current)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      window.cancelAnimationFrame(focusFrame);
      document.removeEventListener('keydown', handleKeyDown);
      previouslyFocused?.focus();
    };
  }, [handleClose, options]);

  return (
    <ConfirmDialogContext.Provider value={{ confirm }}>
      {children}
      {options && (
        <SafeViewportOverlay
          onClose={() => handleClose(false)}
          closing={closing}
          zIndexClassName="z-[70]"
          spacing="edge"
          className="items-end justify-center sm:items-center"
        >
          <div
            ref={dialogRef}
            tabIndex={-1}
            className={`max-h-[92dvh] w-full max-w-sm overflow-y-auto overscroll-contain rounded-t-2xl border border-gray-700/50 bg-surface-card shadow-xl focus:outline-none sm:max-h-full sm:rounded-lg ${closing ? 'animate-slide-out-down sm:animate-scale-out' : 'animate-slide-up sm:animate-scale-in'}`}
            onClick={(e) => e.stopPropagation()}
            role="alertdialog"
            aria-modal="true"
            aria-label={options.title ? undefined : 'Confirm action'}
            aria-labelledby={options.title ? 'confirm-dialog-title' : undefined}
            aria-describedby="confirm-dialog-message"
          >
            <div className="px-5 pt-5 pb-4">
              {options.title && (
                <h3 id="confirm-dialog-title" className="font-heading font-bold text-lg text-gray-100 mb-2">
                  {options.title}
                </h3>
              )}
              {/* whitespace-pre-line: multi-line confirmations (e.g. the
                  draft-rankings replace warning) keep their line breaks;
                  single-line messages are unaffected. */}
              <p id="confirm-dialog-message" className="text-sm text-gray-300 leading-relaxed whitespace-pre-line">
                {options.message}
              </p>
            </div>
            <div className="flex border-t border-gray-700/50">
              <button
                ref={cancelButtonRef}
                onClick={() => handleClose(false)}
                className="min-h-11 flex-1 py-3 text-sm font-semibold text-gray-300 hover:bg-primary/50 active:bg-primary/50 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-accent-yellow"
              >
                {options.cancelLabel || 'Cancel'}
              </button>
              <div className="w-px bg-gray-700/50" />
              <button
                onClick={() => handleClose(true)}
                className={`min-h-11 flex-1 py-3 text-sm font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-accent-yellow ${
                  options.destructive
                    ? 'text-red-400 hover:bg-red-500/10 active:bg-red-500/10'
                    : 'text-accent-yellow hover:bg-accent-yellow/10 active:bg-accent-yellow/10'
                }`}
              >
                {options.confirmLabel || 'Confirm'}
              </button>
            </div>
          </div>
        </SafeViewportOverlay>
      )}
    </ConfirmDialogContext.Provider>
  );
}
